import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {memo, type ReactNode, Suspense} from 'react'
import {graphql, useFragment} from 'react-relay'

import {Header} from '../components/list/header/Header'
import {HeaderLoading} from '../components/list/header/HeaderLoading'
import {Search} from '../components/search/Search'
import {useIssueSearchType} from '../hooks/use-issue-search-type'
import {useRouteInfo} from '../hooks/use-route-info'
import {useSearchMetrics} from '../hooks/use-search-metrics'
import type {ListCurrentViewFragment$key} from './__generated__/ListCurrentViewFragment.graphql'
import type {ListQuery$key} from './__generated__/ListQuery.graphql'
import type {ListRepositoryFragment$key} from './__generated__/ListRepositoryFragment.graphql'
import styles from './List.module.css'
import type {LoadSearchQuery} from './shared'

export type ListProps = {
  fetchedView: ListCurrentViewFragment$key
  queryFromCustomView?: string | null
  onSidePanelNavigate?: (issue: ItemIdentifier) => void
  showSsoBanner?: boolean
  fetchedRepository: ListRepositoryFragment$key | null
  search?: ListQuery$key
  loadSearchQuery?: LoadSearchQuery
  onCollapse?: () => void
  onListItemsChange?: (items: ItemIdentifier[]) => void
  /**
   * Render-prop escape hatch for placing the header, filter row, and
   * results region into separate landmark slots (e.g.
   * `<FilteredListPageLayout.FilterBar>` and
   * `<FilteredListPageLayout.Results>`). When provided, List does NOT
   * render its default `<div>` container — the consumer owns layout. When
   * omitted, behavior is unchanged. During the partial-data/loading window
   * List renders only `header` and does not invoke `renderSlotted` (so the
   * filter and results slots are absent until data resolves). See
   * `IssueIndexPage` for a canonical usage.
   */
  renderSlotted?: (parts: {header: ReactNode; filterBar: ReactNode; results: ReactNode}) => ReactNode
}

const ListInternal = ({
  fetchedView,
  fetchedRepository,
  search,
  loadSearchQuery,
  queryFromCustomView,
  onCollapse,
  onSidePanelNavigate,
  showSsoBanner,
  onListItemsChange,
  renderSlotted,
}: ListProps) => {
  const currentViewData = useFragment<ListCurrentViewFragment$key>(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment ListCurrentViewFragment on Shortcutable {
        ...SearchCurrentViewFragment
        ...HeaderCurrentViewFragment
      }
    `,
    fetchedView,
  )

  const repoData = useFragment<ListRepositoryFragment$key>(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment ListRepositoryFragment on Repository {
        ...SearchRepositoryFragment
      }
    `,
    fetchedRepository || null,
  )

  const searchData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment ListQuery on Searchable
      @argumentDefinitions(
        query: {type: "String!"}
        first: {type: "Int"}
        labelPageSize: {type: "Int!"}
        skip: {type: "Int", defaultValue: null}
        fetchRepository: {type: "Boolean!"}
        type: {type: "SearchType", defaultValue: ISSUE_ADVANCED}
        showIssueFieldPills: {type: "Boolean!", defaultValue: false}
      ) {
        ...SearchRootFragment
          @arguments(
            query: $query
            first: $first
            labelPageSize: $labelPageSize
            skip: $skip
            fetchRepository: $fetchRepository
            type: $type
            showIssueFieldPills: $showIssueFieldPills
          )
      }
    `,
    search,
  )

  const {itemIdentifier} = useRouteInfo()

  const {trackIssueNavigation, updateIssueSearchType} = useSearchMetrics()

  const issueSearchType = useIssueSearchType()

  const header = currentViewData ? (
    <Suspense fallback={<HeaderLoading />}>
      <Header currentViewKey={currentViewData} onCollapse={onCollapse} />
    </Suspense>
  ) : null

  if (!currentViewData || !searchData) {
    // During the partial-data/loading window we still surface the header so
    // the slotted path doesn't render a blank page (the consumer's landmark
    // wrappers are skipped until filterBar/results exist).
    return renderSlotted ? <>{header}</> : <div className={styles.listContainer}>{header}</div>
  }

  const searchElement = (
    <Search
      itemIdentifier={itemIdentifier}
      currentViewKey={currentViewData}
      currentRepository={repoData ?? null}
      search={searchData}
      loadSearchQuery={loadSearchQuery}
      queryFromCustomView={queryFromCustomView}
      onSidePanelNavigate={onSidePanelNavigate}
      showSsoBanner={showSsoBanner}
      trackIssueNavigation={trackIssueNavigation}
      updateIssueSearchType={updateIssueSearchType}
      onListItemsChange={onListItemsChange}
      searchType={issueSearchType}
      renderSlotted={renderSlotted ? ({filterBar, results}) => renderSlotted({header, filterBar, results}) : undefined}
    />
  )

  return renderSlotted ? (
    searchElement
  ) : (
    <div className={styles.listContainer}>
      {header}
      {searchElement}
    </div>
  )
}

export const List = memo(ListInternal)
